/**
 * Where our swatch fell inside the ball band's recommended range.
 *
 * The card already prints both figures, as "20 to 24 sts / 4 in" and then "22 sts and
 * 30 rows / 4 in" three lines further down, and side by side like that they are two
 * unrelated numbers a reader has to hold in their head and subtract. They are not
 * unrelated. The band is what the spinner recommends and the swatch is what somebody
 * actually measured, and the interesting fact about a yarn is where the second one
 * landed inside the first.
 *
 * That matters here more than it would on an ordinary shop page, because the whole
 * arrangement this catalogue is half of substitutes yarn on the measured stitch gauge
 * and ignores the band entirely. `substitutionCandidates` ranks on `stsPer4in`, not on
 * `minStsPer4in` and `maxStsPer4in`, and a reader looking at a substitution the
 * workbench proposed should be able to see why: two yarns can quote overlapping bands
 * and swatch three stitches apart.
 *
 * ### The window is wider than the band
 *
 * A track that ran exactly from `min` to `max` would put a swatch at either end of the
 * range on the very edge of the drawing, where a tick is half cut off and reads as an
 * error rather than as a measurement. Two of the seven yarns in this catalogue swatch
 * exactly at their band minimum, so that is the common case and not a corner. The
 * window is padded out on both sides, the band is drawn as a filled span inside it, and
 * the tick sits where it sits.
 *
 * Nothing here renders. The placement is percentages along a track and a sentence about
 * what they mean, so `gauge.test.ts` can check the arithmetic and the edge wording
 * without a DOM, the same split `geometry.ts` makes for the swatch paths.
 */

import type { YarnRecord } from '../catalogue';

/**
 * How far past each end of the band the track runs, in stitches.
 *
 * Two fifths of the band's own width, floored at one stitch so a band quoted as a
 * single value still has somewhere to put a tick. Proportional rather than fixed
 * because the catalogue's bands run from two stitches wide (Cascade 220, 18 to 20) to
 * four (Wool of the Andes, 20 to 24), and a fixed pad would swallow the narrow ones.
 */
const padFor = (min: number, max: number): number => Math.max(1, (max - min) * 0.4);

/** Percentages, rounded to a tenth, which is finer than a rendered pixel. */
const pct = (value: number): number => Math.round(value * 1000) / 10;

/** Which end of the band a swatch landed on, or that it landed off the band. */
export type GaugeStanding =
  /** Comfortably inside, away from both ends. */
  | 'inside'
  /** On the band's low stitch count, which is its loose, coarse end. */
  | 'loose-end'
  /** On the band's high stitch count, which is its tight, fine end. */
  | 'tight-end'
  /** Fewer stitches than the band's lowest, so looser than recommended. */
  | 'looser'
  /** More stitches than the band's highest, so tighter than recommended. */
  | 'tighter';

export interface GaugePlacement {
  /** Left edge of the recommended band, as a percentage of the track. */
  readonly bandStart: number;
  /** Width of the recommended band, as a percentage of the track. */
  readonly bandWidth: number;
  /** Where the measured swatch sits, as a percentage of the track. */
  readonly swatchAt: number;
  readonly standing: GaugeStanding;
  /** Whether the swatch fell outside the band at all. */
  readonly outside: boolean;
  /** The two ends of the window, printed as the track's own scale labels. */
  readonly low: number;
  readonly high: number;
}

/**
 * The band, the tick and what the pair of them amount to.
 *
 * A swatch outside the band is clamped to the end of the track it went past rather
 * than being drawn off the edge, and `outside` says that happened, so the component
 * can label it instead of quietly drawing a tick that is a lie about how far out it
 * went. None of the seven yarns on file is outside its band today; a yarn added later
 * could be, and this is a shop page, so the honest answer is to say so rather than to
 * assume it cannot happen.
 */
export function gaugePlacement(yarn: YarnRecord): GaugePlacement {
  const min = Math.min(yarn.minStsPer4in, yarn.maxStsPer4in);
  const max = Math.max(yarn.minStsPer4in, yarn.maxStsPer4in);
  const pad = padFor(min, max);

  const low = min - pad;
  const high = max + pad;
  const span = high - low;

  const along = (sts: number): number => Math.min(1, Math.max(0, (sts - low) / span));

  const measured = yarn.stsPer4in;
  const standing: GaugeStanding =
    measured < min
      ? 'looser'
      : measured > max
        ? 'tighter'
        : measured === min
          ? 'loose-end'
          : measured === max
            ? 'tight-end'
            : 'inside';

  return {
    bandStart: pct(along(min)),
    bandWidth: pct(along(max) - along(min)),
    swatchAt: pct(along(measured)),
    standing,
    outside: standing === 'looser' || standing === 'tighter',
    low,
    high,
  };
}

/**
 * What the placement means, in a knitter's terms.
 *
 * One sentence, and it names the numbers rather than describing the picture, because a
 * reader using a screen reader gets this text and no picture at all. "Loose" and
 * "tight" rather than "low" and "high": a band's low stitch count is its loose end, and
 * nobody at a yarn shop says low.
 */
export function gaugeStandingText(yarn: YarnRecord): string {
  const { standing } = gaugePlacement(yarn);
  const measured = `${yarn.stsPer4in} sts`;
  const band = `${yarn.minStsPer4in} to ${yarn.maxStsPer4in}`;

  switch (standing) {
    case 'inside':
      return `Our swatch came out at ${measured}, inside the ${band} the band recommends.`;
    case 'loose-end':
      return `Our swatch came out at ${measured}, right on the loose end of the ${band} band.`;
    case 'tight-end':
      return `Our swatch came out at ${measured}, right on the tight end of the ${band} band.`;
    case 'looser':
      return `Our swatch came out at ${measured}, looser than the ${band} the band recommends.`;
    case 'tighter':
      return `Our swatch came out at ${measured}, tighter than the ${band} the band recommends.`;
  }
}
